import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, InlineLoading } from 'carbon-components-react';
import {
  ChevronLeft20,
  Star20,
  StarFilled20,
  Phone16,
  Email16,
  ViewOff20,
  View20
} from '@carbon/icons-react';
import { useComponentMount } from 'utilities/hooks.util';
import { phoneNumberFormatter } from 'utilities/validation';
import Utils from 'utilities/helper.util';
import {
  fetchCLODetails,
  addCLOEvent,
  addCLOAsFavourite,
  removeCLOAsFavourite,
  hideCLO,
  unhideCLO,
  refreshTopThreeCLO
} from 'redux/clo';
import { selectCLODetails, selectLoadingCLODetails } from 'redux/clo/selectors';

type OwnProps = {
  // jobId: String;
  handleBackButtonClick: Function;
  CLODetailsId: number;
};

interface CLODetailsObject {
  name: string;
  logoUrl: string;
  locatorAmount: number;
  serviceAreas: any;
  address: any;
  phoneNumber: string;
  url: string;
  facebookUrl: string;
  facebookPageName: string;
  email: string;
}

const initailCLODetails = {
  name: '',
  logoUrl: '',
  locatorAmount: 0,
  serviceAreas: '',
  address: '',
  phoneNumber: '',
  url: '',
  facebookUrl: '',
  facebookPageName: '',
  email: ''
};

type Props = OwnProps;

const CLOModalDetails: React.FC<Props | any> = (props: Props) => {
  // const { jobId, handleBackButtonClick, CLODetailsId } = props;
  const { handleBackButtonClick, CLODetailsId } = props;
  const dispatch = useDispatch();
  const cloDetails = useSelector(selectCLODetails);
  const loadingCLODetails = useSelector(selectLoadingCLODetails);
  const [
    modifiedCLODetails,
    setModifiedCLODetails
  ] = useState<CLODetailsObject>(initailCLODetails);
  const [isFavourite, setIsFavourite] = useState<boolean>(false);
  const [isHidden, setIsHidden] = useState<boolean>(false);

  const groupServiceAres = (serviceAreas: Array<any>) => {
    const areas: any = {};

    serviceAreas.forEach((item: any) => {
      if (!areas[item.state]) {
        areas[item.state] = item.name;
      } else {
        areas[item.state] = areas[item.state].concat(', ', item.name);
      }
    });

    const sAreas = Object.keys(areas).map(function (key, idx) {
      return (
        <div key={idx} className="service-areas-list">
          {key} - {areas[key]}.
        </div>
      );
    });
    return sAreas;
  };

  const getFacebookPageName = (fbUrl: string) => {
    const fbUrlSplit = fbUrl.split('/');
    if (fbUrlSplit?.length > 0) {
      return fbUrlSplit[fbUrlSplit.length - 1];
    }
    return '';
  };

  const addCLOEventTracking = (eventType: string) => {
    const data = {
      cloId: CLODetailsId,
      eventType
    };
    dispatch(addCLOEvent(data));
  };

  const handleActionButtonClick = (type: string, data: string) => {
    addCLOEventTracking(type);
    const anchor = document.createElement('a');
    if (type === 'CLICK_CALL') {
      anchor.href = `tel:${data}`;
      setTimeout(() => {
        anchor.click();
      }, 500);
    }
    if (type === 'CLICK_EMAIL') {
      anchor.href = `mailTo:${data}`;
      anchor.click();
    }
  };

  const handleStarClick = (addAsFavourite: boolean) => {
    if (addAsFavourite) {
      setIsFavourite(true);
      dispatch(addCLOAsFavourite({ cloId: CLODetailsId, action: 'add' }));
    } else {
      setIsFavourite(false);
      dispatch(removeCLOAsFavourite({ cloId: CLODetailsId, action: 'remove' }));
    }
  };

  const handleHideUnhideClick = (value: boolean) => {
    if (value) {
      setIsHidden(true);
      dispatch(hideCLO({ cloId: CLODetailsId, action: 'hide' }));
    } else {
      setIsHidden(false);
      dispatch(unhideCLO({ cloId: CLODetailsId, action: 'unhide' }));
    }
  };

  const handleBackClick = (backBtnClick: boolean) => {
    setIsFavourite(false);
    handleBackButtonClick();

    if (backBtnClick) {
      dispatch(refreshTopThreeCLO());
    }
  };

  useEffect(() => {
    if (
      !loadingCLODetails &&
      cloDetails &&
      Object.keys(cloDetails).length !== 0
    ) {
      const modifiedCLODetails = {
        name: cloDetails.name,
        logoUrl: cloDetails.details.logoUrl,
        locatorAmount: cloDetails.details.locatorAmount,
        serviceAreas: groupServiceAres(cloDetails.ServiceAreas),
        address: cloDetails.Address,
        phoneNumber: cloDetails.details.phoneNumber,
        url: cloDetails.details.url,
        facebookUrl: cloDetails.details.facebookUrl,
        facebookPageName: cloDetails.details.facebookUrl
          ? getFacebookPageName(cloDetails.details.facebookUrl)
          : '',
        email: cloDetails.details.email
      };
      setModifiedCLODetails(modifiedCLODetails);
      setIsFavourite(cloDetails?.Favourites.length > 0);
      setIsHidden(cloDetails?.Hidden.length > 0);
    }
    if (loadingCLODetails) {
      setIsFavourite(false);
      setIsHidden(false);
    }
  }, [cloDetails, loadingCLODetails]);

  useComponentMount(() => {
    dispatch(fetchCLODetails(CLODetailsId));
    addCLOEventTracking('SHOW_DETAIL');
  });

  return (
    <>
      <div className="navigation-job-id">
        <ChevronLeft20 onClick={() => handleBackClick(true)} />
        <div className="job-id" onClick={() => handleBackClick(true)}>
          Back
        </div>
      </div>
      <div className="clo-details">
        <div className="details-container">
          {!loadingCLODetails && (
            <div className="loading-preferences-container">
              <div></div>
              <div className="preferences-container">
                {loadingCLODetails && <div className="blocker" />}
                <div className="hide-preferences-container">
                  {isHidden ? (
                    <div
                      onClick={() => handleHideUnhideClick(false)}
                      title={'Unhide organisation from listing'}
                    >
                      Unhide
                      <View20 />
                    </div>
                  ) : (
                    <div
                      onClick={() => handleHideUnhideClick(true)}
                      title={'Hide organisation from listing'}
                    >
                      Hide
                      <ViewOff20 />
                    </div>
                  )}
                </div>
                <div
                  title={
                    isFavourite
                      ? 'Unmark as a favourite'
                      : 'Mark as a favourite'
                  }
                >
                  {isFavourite ? (
                    <StarFilled20
                      className="starred"
                      onClick={() => handleStarClick(false)}
                    />
                  ) : (
                    <Star20
                      className="non-starred"
                      onClick={() => handleStarClick(true)}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
          <div
            className={`details ${
              loadingCLODetails ? 'loading-container' : ''
            }`}
          >
            {loadingCLODetails && <InlineLoading description="Loading" />}
            {!loadingCLODetails &&
              modifiedCLODetails &&
              Object.keys(modifiedCLODetails).length !== 0 && (
                <>
                  <div className="logo-container">
                    {modifiedCLODetails.logoUrl ? (
                      <img
                        src={modifiedCLODetails.logoUrl}
                        alt=""
                        className="logo"
                      />
                    ) : (
                      <div className="initial-logo">
                        {modifiedCLODetails.name.trim().charAt(0)}
                      </div>
                    )}
                  </div>

                  <div className="section">
                    <div className="name">{modifiedCLODetails.name}</div>
                    <div className="number-of-locators">
                      {`${modifiedCLODetails.locatorAmount} Certified Locator${
                        modifiedCLODetails.locatorAmount > 1 ? 's' : ''
                      }`}
                    </div>
                  </div>

                  <div className="section">
                    {modifiedCLODetails.address && (
                      <div className="address">
                        {Utils.getFullAddress(modifiedCLODetails.address)}
                      </div>
                    )}
                    <div className="phone-number">
                      {phoneNumberFormatter({
                        phoneNumber: String(modifiedCLODetails.phoneNumber)
                      }).getNationalNumberFormat()}
                    </div>
                    {modifiedCLODetails.url && (
                      <div className="link">
                        <a
                          href={`${modifiedCLODetails.url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => addCLOEventTracking('CLICK_URL')}
                        >
                          {modifiedCLODetails.url}
                        </a>
                      </div>
                    )}
                  </div>

                  {modifiedCLODetails.facebookUrl && (
                    <div className="section facebook">
                      <div
                        className="facebook-icon"
                        onClick={() =>
                          window.open(
                            modifiedCLODetails.facebookUrl,
                            '_blank',
                            'noopener noreferrer'
                          )
                        }
                      />
                    </div>
                  )}
                </>
              )}
          </div>
          {!loadingCLODetails && (
            <div className="action-buttons">
              <div className="contact">
                <Button
                  kind="tertiary"
                  renderIcon={Phone16}
                  onClick={() =>
                    handleActionButtonClick(
                      'CLICK_CALL',
                      modifiedCLODetails.phoneNumber
                    )
                  }
                  disabled={loadingCLODetails}
                >
                  Call us now
                </Button>
                <Button
                  kind="tertiary"
                  renderIcon={Email16}
                  onClick={() =>
                    handleActionButtonClick(
                      'CLICK_EMAIL',
                      modifiedCLODetails.email
                    )
                  }
                  disabled={loadingCLODetails}
                >
                  Send an email
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CLOModalDetails;
